import React from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { PrioritySectionEntry } from "./DenmarkEditContent";
import { truncateText } from "@/utils/utilFunctions/truncateText";

export type PreviewSaveStatus = "idle" | "loading" | "success" | "error";

export interface PriorityPreviewInlineProps {
  prioritySectionsList: PrioritySectionEntry[];
  selectedChipId: number | string;
  selectedChipTitle: string;
  /** Per-section save status map, controlled by parent */
  saveStatuses: Record<number, PreviewSaveStatus>;
}

/** Returns true only when the API explicitly says a priority list exists. */
const hasPriorityList = (priorityList?: any): boolean =>
  priorityList?.hasPriority === true;

/** Returns 1-based rank of selectedChipId in the section's priorityList, or null if not found. */
const getRank = (
  entry: PrioritySectionEntry,
  selectedChipId: number | string
): number | null => {
  if (!hasPriorityList(entry.priorityList)) return null;
  const rawList: any[] =
    entry.priorityList?.priorityList?.priorityListEntity?.priorityList ?? [];
  const idx = rawList.findIndex((p: any) => p.msid === Number(selectedChipId));
  return idx !== -1 ? idx + 1 : null;
};

/** Returns the priorityTitle stored in the API for the current media, or falls back to selectedChipTitle. */
const getPriorityTitle = (
  entry: PrioritySectionEntry,
  selectedChipId: number | string,
  selectedChipTitle: string
): string => {
  const rawList: any[] =
    entry.priorityList?.priorityList?.priorityListEntity?.priorityList ?? [];
  const found = rawList.find((p: any) => p.msid === Number(selectedChipId));
  return found?.priorityTitle ?? selectedChipTitle;
};

/** Filters sections to only those where the current media has a rank. */
export const getPreviewRows = (
  prioritySectionsList: PrioritySectionEntry[],
  selectedChipId: number | string
): PrioritySectionEntry[] =>
  prioritySectionsList.filter(
    (entry) => getRank(entry, selectedChipId) !== null
  );

const PriorityPreviewInline: React.FC<PriorityPreviewInlineProps> = ({
  prioritySectionsList,
  selectedChipId,
  selectedChipTitle,
  saveStatuses,
}) => {
  const rows = getPreviewRows(prioritySectionsList, selectedChipId);

  return (
    <div className="d-flex flex-column h-100">
      {/* Header */}
      <div className="d-flex align-items-center px-4 py-3 border-bottom flex-shrink-0">
        <h6 className="mb-0 fw-bold" style={{ color: "#1a1a1a" }}>
          Updated Priority
        </h6>
      </div>

      {/* Table */}
      <div className="overflow-auto flex-grow-1">
        {/* Column headers */}
        <div
          className="d-flex align-items-center px-4 py-2 border-bottom fw-semibold"
          style={{
            backgroundColor: "#eef4fb",
            fontSize: "0.82rem",
            color: "#1565c0",
          }}
        >
          <span style={{ flex: "0 0 220px" }}>
            Priority List({rows.length})
          </span>
          <span className="flex-grow-1">Priority Title</span>
          <span style={{ width: "90px", textAlign: "right" }}>Position</span>
          <span style={{ width: "40px" }} />
        </div>

        {rows.length === 0 ? (
          <p
            className="text-center text-muted my-4"
            style={{ fontSize: "0.85rem" }}
          >
            No priority positions set yet.
          </p>
        ) : (
          rows.map((entry) => {
            const rank = getRank(entry, selectedChipId);
            const priorityTitle = getPriorityTitle(
              entry,
              selectedChipId,
              selectedChipTitle
            );
            const status = saveStatuses[entry.sectionId] ?? "idle";

            return (
              <div
                key={entry.sectionId}
                className="d-flex align-items-center px-4 py-3 border-bottom"
                style={{
                  fontSize: "0.84rem",
                  backgroundColor:
                    status === "success"
                      ? "#f0fdf4"
                      : status === "error"
                        ? "#fff5f5"
                        : "#fff",
                  transition: "background-color 0.3s ease",
                }}
              >
                {/* Section name */}
                <div style={{ flex: "0 0 220px", paddingRight: "12px" }}>
                  <div className="fw-semibold text-truncate text-dark">
                    {entry.sectionName}
                  </div>
                </div>

                {/* Priority title */}
                <div
                  className="flex-grow-1 text-truncate"
                  style={{ color: "#495057" }}
                >
                  {truncateText(priorityTitle, 40)}
                </div>

                {/* Rank */}
                <div
                  className="text-end fw-semibold"
                  style={{ width: "90px", color: "#1565c0" }}
                >
                  #{rank}
                </div>

                {/* Per-row status icon */}
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: "40px" }}
                >
                  {status === "loading" && (
                    <Loader2
                      size={17}
                      color="#1565c0"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  )}
                  {status === "success" && (
                    <CheckCircle2 size={17} color="#16a34a" />
                  )}
                  {status === "error" && <XCircle size={17} color="#dc2626" />}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* CSS for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PriorityPreviewInline;
